/* eslint-disable no-underscore-dangle */
import { Subject } from 'rxjs';

import { Backend, PrimitiveType } from '../class/workflow.type';
import { StatefulService } from '@app/service/stateful.service';

export interface Delta {
  add: string[];
  mod: string[];
  rm: string[];
}

export interface FlatData {
  [storageId: string]: PrimitiveType;
}

export type ErrCallback = (err?: Error) => void;

/**
 * Base class for CRUD storage backends
 */
export abstract class StorageBackend {
  public initialized = false;

  /** Backend type ID */
  public abstract readonly type: Backend;

  /** Request array size update on storage service */
  public readonly propagateBundle = new Subject<void>();

  /** Request array size update on storage service */
  public readonly updateArraySizes = new Subject<boolean>();

  /** Async loading is underway */
  private _retrieving: boolean;

  /**
   * Async saving is underway
   */
  private _saving: boolean;

  /**
   * Store flag indicating that the bundle still comes with '/loans/0/...` instead of `/loan/...`
   */
  private convertLoanArray: boolean;

  public statefulService: StatefulService; // ✅ Ensure this is initialized

  public readonly isBrowser: boolean;

  constructor(
    protected readonly protocol: string,
    protected readonly data: FlatData,
    protected readonly arraySizes: { [rootPath: string]: number },
    protected readonly endpointPath: string,
    protected readonly token: string,
    statefulService: StatefulService,
    isBrowser = false
  ) {
    this.statefulService = statefulService; // ✅ Assign it
    this.isBrowser = isBrowser;
  }

  /**
   * Whether an async operation is underway
   */
  public get isLoading(): boolean {
    return !!(this.retrieving || this.saving);
  }

  protected get retrieving(): boolean {
    return this._retrieving;
  }

  protected set retrieving(value: boolean) {
    this._retrieving = value;
  }

  protected get saving(): boolean {
    return this._saving;
  }

  protected set saving(value: boolean) {
    this._saving = value;
  }

  /**
   * Load field data from backend
   * @param path backend id (without protocol)
   * @param callback function ref to handle error or incoming data
   */
  public abstract load(path: string, callback: (err?: Error, data?: PrimitiveType) => void): void;

  /**
   * Save field data to backend
   * @param path backend id (without protocol)
   * @param callback function ref to handle success or error message
   */
  public abstract save(path: string, data: PrimitiveType, callback: ErrCallback): void;

  /**
   * Initialize storage if necessary
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public abstract init(): Promise<any> | void;

  /**
   * clear storage and data
   */
  public abstract clear(): void;

  /**
   * Sync .data from session storage
   */
  protected syncDump(data: FlatData): void {
    for (const key of Object.keys(this.data)) {
      const [keyProtocol, path] = key.split(':');
      if (this.protocol === keyProtocol && !data.hasOwnProperty(path)) {
        delete this.data[key];
      }
    }
    const currentSessionValue = this.statefulService.wrap('ss:current-patch-request-body').valueString;
    for (const [key, value] of Object.entries(data)) {
      // Only the sessionStorage reconciliation needs the browser
      this.data[`${this.protocol}:${key}`] = this.isBrowser ? this.checkSessionStorage(currentSessionValue, key, value) : value;
    }
    // Always notify listeners; client code expects this
    this.updateArraySizes.next(true);
  }

  /**
   * Method to check & return the storage/path value if it's available in the Session Storage
   * @param currentSessionValue - current value from Session Storage
   * @param currentKey - current storage path/key
   * @param bundleValue - storage path/key value from the bundle
   * @returns
   */
  protected checkSessionStorage(currentSessionValue: string, currentKey: string, bundleValue: PrimitiveType): PrimitiveType {
    let parsedSessionValue = [];

    if (currentSessionValue && currentSessionValue.length > 0) {
      parsedSessionValue = JSON.parse(currentSessionValue);
      const isValueInSession = parsedSessionValue.filter((item) => this.pathRemapForUI(item.path.toString()) === currentKey);

      return isValueInSession.length > 0 ? isValueInSession[isValueInSession.length - 1].value : bundleValue;
    } else {
      return bundleValue;
    }
  }

  /**
   * Sync data after PATCH call is successful
   * @param data - Storage element(s) along with value
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected patchSyncDump(data: any): void {
    if (data.length) {
      data.forEach((element) => {
        this.data[`${this.protocol}:${element.storageId}`] = element.value;
      });
    }
    this.updateArraySizes.next(true);
  }

  /**
   * Moved this from crud-api-backend.ts
   * Bridge gap between API and UI pathes
   * This should be a temporary measure
   * @param path path to check and fix up as necessary
   */
  protected pathRemapForAPI(path: string): string {
    // loan/ -> loans/0
    const parts = path.split('/');
    if (this.convertLoanArray && parts[0] === 'loan') {
      return 'loans/0/' + parts.slice(1).join('/');
    } else if (this.convertLoanArray && parts[0] === 'coBorrowers' && parts[1] === '#' && parts[2] === 'loan') {
      return 'coBorrowers/#/loans/0/' + parts.slice(3).join('/');
    }
    return path;
  }

  /**
   * Moved this from crud-api-backend.ts
   * Bridge gap between API and UI pathes
   * This should be a temporary measure
   * @param path path to check and fix up as necessary
   */
  protected pathRemapForUI(path: string): string {
    // loans/0/ -> loan/
    const parts = path.split('/');
    if (parts[0] === 'loans' && parts[1] === '0') {
      this.convertLoanArray = true;
      return 'loan/' + parts.slice(2).join('/');
    } else if (parts[0] === 'coBorrowers' && parts[1] === '#' && parts[2] === 'loans' && parts[3] === '0') {
      this.convertLoanArray = true;
      return 'coBorrowers/#/loan/' + parts.slice(3).join('/');
    }
    return path;
  }
}
